"use client";

import { useState } from "react";
import { Modal, ModalContent, ModalBody, Button, Input, Switch } from "@nextui-org/react";
import { Upload } from "lucide-react";

interface CapacityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (capacity: number | "unlimited", hasWaitlist: boolean) => void;
  initialCapacity?: number;
  initialHasWaitlist?: boolean;
}

export function CapacityModal({ 
  isOpen, 
  onClose, 
  onSave,
  initialCapacity,
  initialHasWaitlist = false
}: CapacityModalProps) {
  const [capacity, setCapacity] = useState<string>(initialCapacity?.toString() || "50");
  const [hasWaitlist, setHasWaitlist] = useState(initialHasWaitlist);

  const handleSave = () => {
    const capacityNumber = parseInt(capacity);
    if (isNaN(capacityNumber) || capacityNumber < 1) {
      return;
    }
    onSave(capacityNumber, hasWaitlist);
    onClose();
  };

  const handleRemoveLimit = () => {
    onSave("unlimited", false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      className="bg-[#1C1B1F] dark"
      classNames={{
        backdrop: "bg-[#1C1B1F]/50 backdrop-blur-sm",
      }}
    >
      <ModalContent>
        <ModalBody className="p-8">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-center">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center">
                <Upload className="w-8 h-8" />
              </div>
            </div>

            <div className="text-center">
              <h2 className="text-2xl font-semibold mb-2">Max Capacity</h2>
              <p className="text-gray-400">
                Auto-close registration when the capacity is reached. Only approved guests count toward the cap.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm mb-2 block">Capacity</label>
                <Input
                  type="number"
                  value={capacity}
                  onChange={(e) => setCapacity(e?.target?.value)}
                  min={1}
                  variant="bordered"
                  classNames={{
                    input: "bg-transparent",
                    inputWrapper: "bg-transparent",
                  }}
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm">Over-Capacity Waitlist</span>
                <Switch
                  isSelected={hasWaitlist}
                  onValueChange={setHasWaitlist}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                color="primary"
                size="lg"
                className="w-full"
                onPress={handleSave}
              >
                Set Limit
              </Button>
              <Button
                variant="flat"
                size="lg"
                className="w-full"
                onPress={handleRemoveLimit}
              >
                Remove Limit
              </Button>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}